import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Eye } from "react-feather";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { API_BASE, supportApi } from "../../services/AxiosInstance";
import "./SupportStatusPage.scss";

const STATUS_BADGE_CLASS = {
  OPEN: "jobseeker-support__badge--status-open",
  IN_PROGRESS: "jobseeker-support__badge--status-progress",
  RESOLVED: "jobseeker-support__badge--status-resolved",
  CLOSED: "jobseeker-support__badge--status-closed",
};

const PRIORITY_BADGE_CLASS = {
  HIGH: "jobseeker-support__badge--priority-high",
  MEDIUM: "jobseeker-support__badge--priority-medium",
  LOW: "jobseeker-support__badge--priority-low",
};

const STATUS_LABEL = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
};

const PRIORITY_LABEL = {
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};

const DEFAULT_FILTERS = {
  search: "",
  status: "",
  priority: "",
  date_from: "",
  date_to: "",
};

function countActiveFilters(filters) {
  if (!filters) return 0;

  let count = 0;
  if (String(filters.search || "").trim()) count += 1;
  if (String(filters.status || "").trim()) count += 1;
  if (String(filters.priority || "").trim()) count += 1;
  if (String(filters.date_from || "").trim()) count += 1;
  if (String(filters.date_to || "").trim()) count += 1;
  return count;
}

function formatTicketDate(value) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return String(value);
  }
}

function getShortTitle(ticket) {
  const issueLabel = String(ticket?.issue_label || "").trim();
  if (issueLabel) return issueLabel;

  const description = String(ticket?.description || "").trim();
  if (!description) return "No title provided";

  return description.length > 64 ? `${description.slice(0, 61).trim()}...` : description;
}

function StatusBadge({ value }) {
  const key = String(value || "").toUpperCase();
  const className = STATUS_BADGE_CLASS[key] || "jobseeker-support__badge--status-default";
  const label = STATUS_LABEL[key] || value || "Unknown";

  return <span className={`jobseeker-support__badge ${className}`}>{label}</span>;
}

function PriorityBadge({ value }) {
  const key = String(value || "").toUpperCase();
  const className = PRIORITY_BADGE_CLASS[key] || "jobseeker-support__badge--priority-default";
  const label = PRIORITY_LABEL[key] || value || "-";

  return <span className={`jobseeker-support__badge ${className}`}>{label}</span>;
}

const SupportStatusPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user_data") || "null");
    } catch (_) {
      return null;
    }
  }, []);

  const email = String(
    userData?.email || userData?.jobseeker_email1 || userData?.recruiter_email || ""
  ).trim();
  const phone = String(
    userData?.phone || userData?.jobseeker_phone1 || userData?.recruiter_phone || ""
  ).trim();
  const apiBase = API_BASE.replace(/\/+$/, "");
  const previewUrl = previewAttachment?.path ? `${apiBase}/${previewAttachment.path}` : "";
  const isPreviewImage = /^image\//i.test(previewAttachment?.mime_type || "");
  const activeFilterCount = countActiveFilters(appliedFilters);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!email && !phone) {
        setError("No jobseeker email or phone found for ticket lookup.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const { data } = await supportApi.getTickets({
          role_key: "jobseeker",
          email: email || undefined,
          phone: phone || undefined,
          search: appliedFilters.search || undefined,
          status: appliedFilters.status || undefined,
          priority: appliedFilters.priority || undefined,
          date_from: appliedFilters.date_from || undefined,
          date_to: appliedFilters.date_to || undefined,
        });
        if (!data?.status) throw new Error(data?.message || "Could not load tickets");
        if (!cancelled) {
          setTickets(Array.isArray(data.data) ? data.data : []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || "Failed to load support tickets");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [email, phone, appliedFilters]);

  const applyFilters = () => {
    setAppliedFilters({ ...draftFilters });
  };

  const resetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  return (
    <Container fluid className="jobseeker-support">
      <Breadcrumbs mainTitle="Help & Support" parent="Jobseeker" title="Track Status" />

      <Row>
        <Col sm="12">
          <Card className="jobseeker-support__card">
            <CardBody className="jobseeker-support__card-body">
              <div className="jobseeker-support__header">
                <div>
                  <h5 className="jobseeker-support__heading mb-1">Your support tickets</h5>
                  <p className="jobseeker-support__subheading mb-0">
                    Showing real ticket data linked to your jobseeker email or phone.
                  </p>
                </div>
                <Link
                  to={`${process.env.PUBLIC_URL}/jobseeker/support`}
                  className="jobseeker-support__help-btn"
                >
                  Need Help
                </Link>
              </div>

              <div className="jobseeker-support__filters">
                <div className="jobseeker-support__filters-head">
                  <div>
                    <div className="jobseeker-support__filters-title">Filters</div>
                    <div className="jobseeker-support__filters-subtitle">
                      Search and refine tickets on the server.
                    </div>
                  </div>
                  {activeFilterCount > 0 ? (
                    <span className="jobseeker-support__filters-pill">
                      {activeFilterCount} active
                    </span>
                  ) : null}
                </div>

                <div className="jobseeker-support__filters-grid">
                  <div className="jobseeker-support__filter-group jobseeker-support__filter-group--wide">
                    <label className="jobseeker-support__filter-label">Search keyword</label>
                    <input
                      type="text"
                      className="jobseeker-support__filter-input"
                      placeholder="Search by problem, title, issue, or description"
                      value={draftFilters.search}
                      onChange={(e) =>
                        setDraftFilters((current) => ({ ...current, search: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyFilters();
                        }
                      }}
                    />
                  </div>

                  <div className="jobseeker-support__filter-group">
                    <label className="jobseeker-support__filter-label">Status</label>
                    <select
                      className="jobseeker-support__filter-input"
                      value={draftFilters.status}
                      onChange={(e) =>
                        setDraftFilters((current) => ({ ...current, status: e.target.value }))
                      }
                    >
                      <option value="">All statuses</option>
                      <option value="OPEN">Open</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="CLOSED">Closed</option>
                    </select>
                  </div>

                  <div className="jobseeker-support__filter-group">
                    <label className="jobseeker-support__filter-label">Priority</label>
                    <select
                      className="jobseeker-support__filter-input"
                      value={draftFilters.priority}
                      onChange={(e) =>
                        setDraftFilters((current) => ({ ...current, priority: e.target.value }))
                      }
                    >
                      <option value="">All priorities</option>
                      <option value="HIGH">High</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="LOW">Low</option>
                    </select>
                  </div>

                  <div className="jobseeker-support__filter-group">
                    <label className="jobseeker-support__filter-label">Created from</label>
                    <input
                      type="date"
                      className="jobseeker-support__filter-input"
                      value={draftFilters.date_from}
                      onChange={(e) =>
                        setDraftFilters((current) => ({ ...current, date_from: e.target.value }))
                      }
                    />
                  </div>

                  <div className="jobseeker-support__filter-group">
                    <label className="jobseeker-support__filter-label">Created to</label>
                    <input
                      type="date"
                      className="jobseeker-support__filter-input"
                      value={draftFilters.date_to}
                      onChange={(e) =>
                        setDraftFilters((current) => ({ ...current, date_to: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="jobseeker-support__filter-actions">
                  <button
                    type="button"
                    className="jobseeker-support__filter-btn jobseeker-support__filter-btn--ghost"
                    onClick={resetFilters}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="jobseeker-support__filter-btn jobseeker-support__filter-btn--primary"
                    onClick={applyFilters}
                  >
                    Apply filters
                  </button>
                </div>
              </div>

              {(email || phone) && (
                <div className="jobseeker-support__context mb-3">
                  {email ? `Email: ${email}` : ""}
                  {email && phone ? " | " : ""}
                  {phone ? `Phone: ${phone}` : ""}
                </div>
              )}

              {error && <Alert color="danger">{error}</Alert>}

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : tickets.length === 0 ? (
                <Alert color="light" className="jobseeker-support__empty mb-0">
                  No support tickets found yet.
                </Alert>
              ) : (
                <div className="jobseeker-support__table-wrap">
                  <div className="table-responsive jobseeker-support__table-scroll">
                    <table className="table align-middle jobseeker-support__table mb-0">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Title</th>
                          <th>Category</th>
                          <th>Problem</th>
                          <th>Attachment</th>
                          <th>Status</th>
                          <th>Priority</th>
                          <th>Created date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map((ticket) => {
                          const cid = ticket.reply_chat_id != null ? Number(ticket.reply_chat_id) : 0;
                          const hasReplyChat = Number.isFinite(cid) && cid > 0;
                          const title = getShortTitle(ticket);
                          const description = String(ticket.description || "").trim();

                          return (
                            <tr key={ticket.ticket_id}>
                              <td className="jobseeker-support__cell-id">#{ticket.ticket_id}</td>
                              <td>
                                <div className="jobseeker-support__title" title={title}>
                                  {title}
                                </div>
                                <div
                                  className="jobseeker-support__desc"
                                  title={description || "No description provided"}
                                >
                                  {description || "No description provided"}
                                </div>
                              </td>
                              <td>{ticket.issue_label || ticket.issue_key || "-"}</td>
                              <td>{ticket.problem_label || ticket.problem_key || "-"}</td>
                              <td>
                                {ticket.attachment?.path ? (
                                  <button
                                    type="button"
                                    className="jobseeker-support__icon-btn"
                                    title={ticket.attachment.original_name || "View attachment"}
                                    onClick={() => setPreviewAttachment(ticket.attachment)}
                                  >
                                    <Eye size={14} />
                                  </button>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>
                                <StatusBadge value={ticket.status} />
                              </td>
                              <td>
                                <PriorityBadge value={ticket.priority} />
                              </td>
                              <td className="jobseeker-support__date">
                                {formatTicketDate(ticket.created_at)}
                              </td>
                              <td>
                                {hasReplyChat ? (
                                  <Link
                                    to={`${process.env.PUBLIC_URL || ""}/jobseeker/chat`}
                                    state={{ openChatId: cid }}
                                    className="jobseeker-support__reply-btn"
                                  >
                                    Reply
                                  </Link>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={Boolean(previewAttachment)}
        toggle={() => setPreviewAttachment(null)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setPreviewAttachment(null)}>
          {previewAttachment?.original_name || "Attachment Preview"}
        </ModalHeader>
        <ModalBody>
          {previewAttachment ? (
            isPreviewImage ? (
              <img
                src={previewUrl}
                alt={previewAttachment.original_name || "Attachment"}
                style={{ width: "100%", maxHeight: "75vh", objectFit: "contain" }}
              />
            ) : (
              <div className="text-center py-4">
                <p className="mb-3">Preview is not available for this file type.</p>
                <a href={previewUrl} target="_blank" rel="noreferrer" className="btn btn-primary">
                  Open attachment
                </a>
              </div>
            )
          ) : null}
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default SupportStatusPage;
